/**
 * Configuration for onboarding steps including icons, descriptions, and actions.
 * This file serves as the single source of truth for onboarding step metadata.
 */

import {
  IconBulb,
  IconFileText,
  IconGift,
  IconPlugConnected,
  IconSettings,
  IconSparkles,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import type {
  CustomizeOneClickMetadata,
  ImportClientMetadata,
  IntakeDocsMetadata,
  IntegrationMetadata,
  OnboardingStepId,
  OnboardingStepMetadata,
  ReferralMetadata,
  SampleClientMetadata,
  TeamMembersMetadata,
  WizzyDoMetadata,
} from "../types";

export interface OnboardingStepConfig {
  id: OnboardingStepId;
  title: string;
  description: string;
  icon: typeof IconSparkles;
  iconColor: string;
  gradient: string;
  actionLabel: string;
  // Functions to generate dynamic titles/descriptions based on metadata
  getDynamicTitle?: (metadata: OnboardingStepMetadata) => string;
  getDynamicDescription?: (metadata: OnboardingStepMetadata) => string;
}

/**
 * Onboarding step configurations mapped by step ID
 * Add new steps here as they are created
 */
export const ONBOARDING_STEPS_CONFIG: Record<
  OnboardingStepId,
  OnboardingStepConfig
> = {
  SAMPLE_CLIENT: {
    id: "SAMPLE_CLIENT",
    title: "Try Sample Client",
    description: "Try One-Click on a sample client and watch the Wizzy go!",
    icon: IconSparkles,
    iconColor: "#9b59b6",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    actionLabel: "Try Demo",
    getDynamicTitle: (metadata: OnboardingStepMetadata) => {
      const sampleMeta = metadata as SampleClientMetadata;
      if (sampleMeta?.sampleClients && sampleMeta.sampleClients.length > 0) {
        const firstName = sampleMeta.sampleClients[0];
        return `Sample Client: ${firstName}`;
      }
      return "Try Sample Client";
    },
  },
  INTEGRATION: {
    id: "INTEGRATION",
    title: "Connect Integration",
    description: "Link your intake software to automatically pull client data.",
    icon: IconPlugConnected,
    iconColor: "#3498db",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    actionLabel: "Connect Now",
    getDynamicTitle: (metadata: OnboardingStepMetadata) => {
      const integrationMeta = metadata as IntegrationMetadata;
      if (integrationMeta?.integrationName) {
        const softwareName = integrationMeta.integrationName
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        return `Connect ${softwareName}`;
      }
      return "Connect Integration";
    },
  },
  IMPORT_CLIENT: {
    id: "IMPORT_CLIENT",
    title: "Import Your First Client",
    description: "Import client data from your connected integration.",
    icon: IconUserPlus,
    iconColor: "#16a34a",
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    actionLabel: "Import Client",
    getDynamicTitle: (metadata: OnboardingStepMetadata) => {
      const importMeta = metadata as ImportClientMetadata;
      if (importMeta?.integrationName) {
        const softwareName = importMeta.integrationName
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        return `Import from ${softwareName}`;
      }
      return "Import Your First Client";
    },
    getDynamicDescription: (metadata: OnboardingStepMetadata) => {
      const importMeta = metadata as ImportClientMetadata;
      if (importMeta?.hasClients) {
        return "Great! You've imported your first client. Keep going!";
      }
      return "Follow the guide to import client data from your integration.";
    },
  },
  CUSTOMIZE_ONECLICK: {
    id: "CUSTOMIZE_ONECLICK",
    title: "Customize One-Click",
    description: "Customize how you would like Wizzy to enter the tax return.",
    icon: IconSettings,
    iconColor: "#e67e22",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    actionLabel: "Customize",
    getDynamicTitle: (metadata: OnboardingStepMetadata) => {
      const oneClickMeta = metadata as CustomizeOneClickMetadata;
      if (oneClickMeta?.softwareType) {
        let softwareName = "";
        switch (oneClickMeta.softwareType) {
          case "taxwise":
            softwareName = "TaxWise";
            break;
          case "taxslayer":
            softwareName = "TaxSlayer";
            break;
          default:
            softwareName = "One-Click";
        }
        return `One-Click for ${softwareName}`;
      }
      return "Customize One-Click";
    },
  },
  WIZZY_DO: {
    id: "WIZZY_DO",
    title: "What Can Wizzy Do?",
    description:
      "Understand exactly what Wizzy does, doesn't do, and is working on learning.",
    icon: IconBulb,
    iconColor: "#f39c12",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    actionLabel: "Learn More",
    getDynamicDescription: (metadata: OnboardingStepMetadata) => {
      const wizzyMeta = metadata as WizzyDoMetadata;
      if (wizzyMeta?.supportedForms && wizzyMeta.supportedForms.length > 0) {
        const formCount = wizzyMeta.supportedForms.length;
        const firstForms = wizzyMeta.supportedForms.slice(0, 3).join(", ");
        return `Wizzy can automatically fill ${formCount}+ tax forms including ${firstForms}, and more. Discover all capabilities!`;
      }
      return "Discover all the powerful features and capabilities of your AI tax assistant Wizzy.";
    },
  },
  TEAM_MEMBERS: {
    id: "TEAM_MEMBERS",
    title: "Invite Team Members",
    description:
      "Invite members of your team to share clients and allocate your returns to them.",
    icon: IconUsers,
    iconColor: "#2ecc71",
    gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
    actionLabel: "Invite Team",
    getDynamicDescription: (metadata: OnboardingStepMetadata) => {
      const teamMeta = metadata as TeamMembersMetadata;
      if (teamMeta?.totalCount && teamMeta.totalCount > 0) {
        const leadText =
          teamMeta.teamLeadCount > 0
            ? `${teamMeta.teamLeadCount} team lead${teamMeta.teamLeadCount > 1 ? "s" : ""}`
            : "";
        const memberText =
          teamMeta.teamMemberCount > 0
            ? `${teamMeta.teamMemberCount} team member${teamMeta.teamMemberCount > 1 ? "s" : ""}`
            : "";
        const parts = [leadText, memberText].filter(Boolean);
        return `You've invited ${parts.join(" and ")} to collaborate with you. Continue building your team!`;
      }
      return "Collaborate with your team by inviting colleagues to access your SmartWiz workspace.";
    },
  },
  REFERRAL: {
    id: "REFERRAL",
    title: "Refer & Earn",
    description:
      "Create your unique referral link to earn rewards by referring other tax professionals to SmartWiz.",
    icon: IconGift,
    iconColor: "#e74c3c",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    actionLabel: "Get Referral Link",
    getDynamicDescription: (metadata: OnboardingStepMetadata) => {
      const referralMeta = metadata as ReferralMetadata;
      if (referralMeta?.referralSlug) {
        return `Share your referral link with other tax professionals and earn rewards for every successful referral. Your unique slug is ready to share!`;
      }
      return "Earn rewards by referring other tax professionals to SmartWiz. Share your unique referral link and get rewarded for every successful referral.";
    },
  },
  INTAKE_DOCS: {
    id: "INTAKE_DOCS",
    title: "View Integration Docs",
    description:
      "Learn how to set up your intake software integration with our step-by-step documentation.",
    icon: IconFileText,
    iconColor: "#3498db",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    actionLabel: "View Docs",
    getDynamicTitle: (metadata: OnboardingStepMetadata) => {
      const intakeMeta = metadata as IntakeDocsMetadata;
      if (intakeMeta?.intakeSoftware) {
        const softwareName = intakeMeta.intakeSoftware
          .split(" ")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        return `View ${softwareName} Docs`;
      }
      return "View Integration Docs";
    },
  },
};

/**
 * Type guard to check if a string is a valid OnboardingStepId
 * @param stepId - The step ID to validate
 * @returns True if the step ID exists in the configuration
 */
export const isValidStepId = (stepId: string): stepId is OnboardingStepId => {
  return stepId in ONBOARDING_STEPS_CONFIG;
};

/**
 * Get configuration for a specific onboarding step
 * @param stepId - The step ID to look up
 * @returns The step configuration, or undefined if the step ID is invalid
 */
export const getStepConfig = (
  stepId: string
): OnboardingStepConfig | undefined => {
  if (!isValidStepId(stepId)) {
    console.warn(`No config found for step: ${stepId}`);
    return undefined;
  }
  return ONBOARDING_STEPS_CONFIG[stepId];
};

/**
 * Get all step configurations as an array
 */
export const getAllStepConfigs = (): OnboardingStepConfig[] => {
  return Object.values(ONBOARDING_STEPS_CONFIG);
};

/**
 * Filters an array of step items to only include steps with valid configurations
 * Useful for filtering API responses that may contain unknown step IDs
 * @param steps - Array of step items to filter
 * @returns Filtered array containing only steps with valid configurations
 */
export const filterValidSteps = <T extends { id: string }>(steps: T[]): T[] => {
  return steps.filter((step) => {
    const isValid = isValidStepId(step.id);
    if (!isValid) {
      console.warn(
        `Filtering out step with unknown ID: ${step.id}. This step may have been added on the backend but not yet in the frontend config.`
      );
    }
    return isValid;
  });
};

/**
 * Get dynamic title for a step based on metadata
 * Falls back to static title if no dynamic title function is defined
 * @param stepId - The step ID (can be any string, will be validated)
 * @param metadata - Optional metadata for dynamic title generation
 * @returns The step title, or "Unknown Step" if the step ID is invalid
 */
export const getStepTitle = (
  stepId: string,
  metadata?: OnboardingStepMetadata
): string => {
  const config = getStepConfig(stepId);
  if (!config) {
    return "Unknown Step";
  }
  if (metadata && config.getDynamicTitle) {
    return config.getDynamicTitle(metadata);
  }
  return config.title;
};

/**
 * Get dynamic description for a step based on metadata
 * Falls back to static description if no dynamic description function is defined
 * @param stepId - The step ID (can be any string, will be validated)
 * @param metadata - Optional metadata for dynamic description generation
 * @returns The step description, or "No description available" if the step ID is invalid
 */
export const getStepDescription = (
  stepId: string,
  metadata?: OnboardingStepMetadata
): string => {
  const config = getStepConfig(stepId);
  if (!config) {
    return "No description available";
  }
  if (metadata && config.getDynamicDescription) {
    return config.getDynamicDescription(metadata);
  }
  return config.description;
};
